import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { useTranslation } from '../../../hooks/useTranslation'
import coachChatManager from './CoachChatManager'
import errorLogger from '../services/ErrorLogger'

const CoachChatContext = createContext()

export const useCoachChat = () => {
  const context = useContext(CoachChatContext)
  if (!context) {
    throw new Error('useCoachChat must be used within a CoachChatProvider')
  }
  return context
}

export const CoachChatProvider = ({ children, ...props }) => {
  // Translation hook
  const { t } = useTranslation('coach_chat')

  // Coach-specific state
  const [showCandidateList, setShowCandidateList] = useState(false)
  const [selectedCandidate, _setSelectedCandidate] = useState(null)
  const [chatHistory, setChatHistory] = useState([])
  const [isOnline, setIsOnline] = useState(false)
  const [activeChats, setActiveChats] = useState([])
  const [pendingChats, setPendingChats] = useState([])

  // Dark mode state with sessionStorage persistence
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const stored = sessionStorage.getItem('coachChatDarkMode')
      return stored ? JSON.parse(stored) : false
    } catch (error) {
      console.warn(
        'Error reading coachChatDarkMode from sessionStorage:',
        error,
      )
      return false
    }
  })

  // Add state for connection state to make it reactive
  const [connectionState, setConnectionState] = useState(
    coachChatManager.getConnectionState(),
  )

  // Ref for ringtone audio element
  const ringtoneAudioRef = useRef(null)
  // Ref for message notification audio element
  const messageNotificationRef = useRef(null)

  /*
  console.log(
    'CoachChatContext - Initial connectionState from manager:',
    connectionState,
  )
  */

  // Helper function to get ringtone settings from localStorage
  const getRingtoneSettings = useCallback(() => {
    if (typeof window === 'undefined') {
      const fallbackUrl =
        props.ringToneOptions && props.ringToneOptions.length > 0
          ? props.ringToneOptions[0]
          : null
      return { url: fallbackUrl, volume: '0.8' }
    }

    try {
      const ringTone = localStorage.getItem('ring_tone')
      const ringVolume = localStorage.getItem('ring_volume') || '0.8'

      // Use fallback to first option if no ring_tone is stored
      const ringToneUrl =
        ringTone ||
        (props.ringToneOptions && props.ringToneOptions.length > 0
          ? props.ringToneOptions[0]
          : null)

      return {
        url: ringToneUrl,
        volume: ringVolume,
      }
    } catch (error) {
      console.warn('Error reading ringtone settings from localStorage:', error)
      const fallbackUrl =
        props.ringToneOptions && props.ringToneOptions.length > 0
          ? props.ringToneOptions[0]
          : null
      return { url: fallbackUrl, volume: '0.8' }
    }
  }, [props.ringToneOptions])

  // Helper function to play ringtone
  const playRingtone = useCallback(() => {
    const settings = getRingtoneSettings()
    if (!settings.url) return

    const audioElement = ringtoneAudioRef.current
    if (!audioElement) return

    audioElement.volume = parseFloat(settings.volume)
    audioElement.loop = true

    const doPlay = () => {
      audioElement.currentTime = 0
      const promise = audioElement.play()
      if (promise !== undefined) {
        promise.catch(() => {
          // Browsers block autoplay until a real user gesture occurs.
          // Register capture-phase listeners so we catch the next interaction
          // regardless of where on the page it happens.
          const unlock = () => {
            audioElement.play().catch(() => {})
            document.removeEventListener('click', unlock, true)
            document.removeEventListener('keydown', unlock, true)
            document.removeEventListener('touchstart', unlock, true)
          }
          document.addEventListener('click', unlock, {
            capture: true,
            once: true,
          })
          document.addEventListener('keydown', unlock, {
            capture: true,
            once: true,
          })
          document.addEventListener('touchstart', unlock, {
            capture: true,
            once: true,
          })
        })
      }
    }

    if (audioElement.src !== settings.url) {
      // Source changed — reload and wait for the audio to be ready
      audioElement.src = settings.url
      audioElement.load()
      audioElement.oncanplaythrough = () => {
        audioElement.oncanplaythrough = null
        doPlay()
      }
    } else if (audioElement.readyState >= 3) {
      doPlay()
    } else {
      // Same URL but not yet buffered (e.g. slow connection)
      audioElement.oncanplaythrough = () => {
        audioElement.oncanplaythrough = null
        doPlay()
      }
    }
  }, [getRingtoneSettings])

  // Helper function to stop ringtone
  const stopRingtone = useCallback(() => {
    const audioElement = ringtoneAudioRef.current
    if (audioElement) {
      // Disable loop first to prevent it from continuing
      audioElement.loop = false
      // Pause the audio
      audioElement.pause()
      // Reset to beginning
      audioElement.currentTime = 0
      // Remove any event listeners that might restart it
      audioElement.oncanplaythrough = null
      // console.log('CoachChatContext - Ringtone stopped')
    }
  }, [])

  // Helper function to get message sound settings from localStorage.
  // Volume uses the shared ring_volume setting (universal sound volume).
  const getMessageSoundSettings = useCallback(() => {
    if (typeof window === 'undefined') {
      const fallbackUrl =
        props.messageSoundOptions && props.messageSoundOptions.length > 0
          ? props.messageSoundOptions[0]
          : null
      return { url: fallbackUrl, volume: '0.8' }
    }

    try {
      const messageSound = localStorage.getItem('message_sound')
      const soundVolume = localStorage.getItem('ring_volume') || '0.8'

      // Use fallback to first option if no message_sound is stored
      const messageSoundUrl =
        messageSound ||
        (props.messageSoundOptions && props.messageSoundOptions.length > 0
          ? props.messageSoundOptions[0]
          : null)

      return {
        url: messageSoundUrl,
        volume: soundVolume,
      }
    } catch (error) {
      console.warn(
        'Error reading message sound settings from localStorage:',
        error,
      )
      const fallbackUrl =
        props.messageSoundOptions && props.messageSoundOptions.length > 0
          ? props.messageSoundOptions[0]
          : null
      return { url: fallbackUrl, volume: '0.8' }
    }
  }, [props.messageSoundOptions])

  // Helper function to play message notification sound
  const playMessageNotification = useCallback(() => {
    const settings = getMessageSoundSettings()
    if (!settings.url) return

    const audioElement = messageNotificationRef.current
    if (!audioElement) return

    audioElement.volume = parseFloat(settings.volume)

    const doPlay = () => {
      audioElement.currentTime = 0
      const promise = audioElement.play()
      if (promise !== undefined) {
        promise.catch(() => {
          // Browsers block autoplay until a real user gesture occurs.
          const unlock = () => {
            audioElement.play().catch(() => {})
            document.removeEventListener('click', unlock, true)
            document.removeEventListener('keydown', unlock, true)
            document.removeEventListener('touchstart', unlock, true)
          }
          document.addEventListener('click', unlock, {
            capture: true,
            once: true,
          })
          document.addEventListener('keydown', unlock, {
            capture: true,
            once: true,
          })
          document.addEventListener('touchstart', unlock, {
            capture: true,
            once: true,
          })
        })
      }
    }

    if (audioElement.src !== settings.url) {
      audioElement.src = settings.url
      audioElement.load()
      audioElement.oncanplaythrough = () => {
        audioElement.oncanplaythrough = null
        doPlay()
      }
    } else if (audioElement.readyState >= 3) {
      doPlay()
    } else {
      audioElement.oncanplaythrough = () => {
        audioElement.oncanplaythrough = null
        doPlay()
      }
    }
  }, [getMessageSoundSettings])

  // Send welcome message to candidate
  const _sendWelcomeMessage = useCallback(
    (chatData) => {
      try {
        // Get candidate name from chatData
        const candidateName = chatData.candidateName || chatData.candidateId

        // Determine if candidate has a name (not starting with underscore like in old system)
        const hasName = candidateName && !candidateName.match(/^_/)

        // Get the appropriate welcome message using translations
        let welcomeMessage
        if (hasName) {
          // Use the personalized greeting with candidate name
          welcomeMessage = t('welcome_messages.auto_greeting', {
            default: 'Hi, %{candidate_name}. How can I help you today?',
            candidate_name: candidateName,
          })
        } else {
          // Use the generic greeting for nameless candidates
          welcomeMessage = t('welcome_messages.auto_greeting_nameless', {
            default: 'Hi. How can I help you today?',
          })
        }

        // Create a selectedCandidate object from chatData to ensure message goes to correct chat slot
        // chatData is the activeChat object which contains chatSlot, chatKey, candidateEasyrtcid, etc.
        const selectedCandidate = {
          id: chatData.id,
          easyrtcid: chatData.candidateEasyrtcid,
          chatSlot: chatData.chatSlot,
          chatKey: chatData.chatKey,
          candidateId: chatData.candidateId,
          candidateName: chatData.candidateName,
        }

        // Send the welcome message using the chat manager with the correct selectedCandidate
        // This ensures the message goes to the correct chat slot (chat-1 or chat-2)
        coachChatManager.sendMessage(
          welcomeMessage,
          chatData.id,
          selectedCandidate,
        )

        // console.log('CoachChatContext - Sent welcome message:', welcomeMessage)
      } catch (error) {
        console.error(
          'CoachChatContext - Error sending welcome message:',
          error,
        )
      }
    },
    [t],
  )

  // Stop ringtone when there are no more pending chats
  useEffect(() => {
    if (pendingChats.length === 0) {
      stopRingtone()
    }
  }, [pendingChats, stopRingtone])

  // Listen to state changes from ChatManager
  useEffect(() => {
    const handleStateChange = (_newState) => {
      // Get the computed connection state that includes chatStatus
      const computedState = coachChatManager.getConnectionState()
      /*
      console.log(
        'CoachChatContext - handleStateChange called, computedState:',
        computedState,
      )
      */
      // Update the local state with the computed state
      setConnectionState(computedState)
      /*
      console.log(
        'CoachChatContext - Updated connectionState to:',
        computedState,
      )
      */
    }

    const handleIncomingCall = (data) => {
      // console.log('CoachChatContext - handleIncomingCall called with:', data)
      // Handle incoming call from candidate
      setPendingChats((prev) => [...prev, data])
      // Play ringtone when incoming call arrives
      playRingtone()
    }

    const handleChatStarted = (data) => {
      // console.log('CoachChatContext - handleChatStarted called with:', data)
      // Stop ringtone when chat starts
      stopRingtone()
      // Move from pending to active chats
      setPendingChats((prev) => prev.filter((chat) => chat.id !== data.id))
      setActiveChats((prev) => [...prev, data])

      // Send welcome message to candidate
      _sendWelcomeMessage(data)
    }

    const handleChatEnded = (data) => {
      // console.log('CoachChatContext - handleChatEnded called with:', data)
      // Remove from active chats and add to history
      setActiveChats((prev) => prev.filter((chat) => chat.id !== data.id))
      setChatHistory((prev) => [...prev, data])
    }

    const handleMessageReceived = (data) => {
      // console.log('CoachChatContext - handleMessageReceived called with:', data)
      // Update chat messages
      setActiveChats((prev) =>
        prev.map((chat) =>
          chat.id === data.chatId
            ? { ...chat, messages: [...(chat.messages || []), data] }
            : chat,
        ),
      )
      // Play message notification sound only for messages from candidates (not from coach)
      if (data.message?.sender === 'candidate') {
        playMessageNotification()
      }
    }

    // Add event listeners
    // console.log('CoachChatContext - Setting up event listeners')
    coachChatManager.addEventListener('stateChange', handleStateChange)
    coachChatManager.addEventListener('incomingChatRequest', handleIncomingCall)
    coachChatManager.addEventListener('chatStarted', handleChatStarted)
    coachChatManager.addEventListener('chatEnded', handleChatEnded)
    coachChatManager.addEventListener('peerMessage', handleMessageReceived)

    // Add listeners for connection status updates
    coachChatManager.addEventListener('connected', handleStateChange)
    coachChatManager.addEventListener('disconnected', handleStateChange)
    coachChatManager.addEventListener('presenceUpdated', handleStateChange)

    // Add listeners for chat events
    coachChatManager.addEventListener('dataChannelOpen', handleStateChange)
    coachChatManager.addEventListener('candidateTyping', handleStateChange)
    coachChatManager.addEventListener('availabilityChanged', handleStateChange)

    const handleChatDeclined = (data) => {
      // Stop ringtone when chat is declined
      stopRingtone()
      // Also remove from pending chats if it's there
      setPendingChats((prev) =>
        prev.filter((chat) => chat.candidateEasyrtcid !== data.candidateId),
      )
      handleStateChange(data)
    }
    coachChatManager.addEventListener('chatDeclined', handleChatDeclined)

    const handleCallCancelled = (data) => {
      // Stop ringtone when call is cancelled by candidate
      stopRingtone()
      // Remove from pending chats
      setPendingChats((prev) =>
        prev.filter(
          (chat) => chat.candidateEasyrtcid !== data.candidateEasyrtcid,
        ),
      )
      handleStateChange(data)
    }
    coachChatManager.addEventListener('callCancelled', handleCallCancelled)

    const handleCallMissed = (data) => {
      // Stop ringtone when call is missed
      stopRingtone()
      // Remove from pending chats
      setPendingChats((prev) =>
        prev.filter(
          (chat) => chat.candidateEasyrtcid !== data.candidateEasyrtcid,
        ),
      )
      handleStateChange(data)
    }
    coachChatManager.addEventListener('callMissed', handleCallMissed)

    // Add listeners for list updates
    coachChatManager.addEventListener('coachesUpdate', handleStateChange)
    coachChatManager.addEventListener('candidatesUpdate', handleStateChange)
    coachChatManager.addEventListener('coachListUpdated', handleStateChange)
    coachChatManager.addEventListener('candidateInfoUpdated', handleStateChange)

    // Add listener for candidate media state changes
    const handleCandidateMediaStateChange = (data) => {
      // console.log(
      //   'CoachChatContext - candidateMediaStateChanged event received:',
      //   data,
      // )
      // Update active chats with the new media state
      setActiveChats((prev) =>
        prev.map((chat) =>
          chat.id === data.chatId
            ? {
                ...chat,
                candidateVideoPaused:
                  data.messageType === 'video_paused' ||
                  data.messageType === 'video_unpaused'
                    ? data.messageType === 'video_paused'
                    : chat.candidateVideoPaused,
                candidateAudioMuted:
                  data.messageType === 'audio_muted' ||
                  data.messageType === 'audio_unmuted'
                    ? data.messageType === 'audio_muted'
                    : chat.candidateAudioMuted,
              }
            : chat,
        ),
      )
    }
    coachChatManager.addEventListener(
      'candidateMediaStateChanged',
      handleCandidateMediaStateChange,
    )

    // Initial state setup
    const initialState = coachChatManager.getConnectionState()
    // console.log('CoachChatContext - Initial state from manager:', initialState)
    setConnectionState(initialState)

    return () => {
      coachChatManager.removeEventListener('stateChange', handleStateChange)
      coachChatManager.removeEventListener(
        'incomingChatRequest',
        handleIncomingCall,
      )
      coachChatManager.removeEventListener('chatStarted', handleChatStarted)
      coachChatManager.removeEventListener('chatEnded', handleChatEnded)
      coachChatManager.removeEventListener('peerMessage', handleMessageReceived)

      // Remove listeners for connection status updates
      coachChatManager.removeEventListener('connected', handleStateChange)
      coachChatManager.removeEventListener('disconnected', handleStateChange)
      coachChatManager.removeEventListener('presenceUpdated', handleStateChange)

      // Remove listeners for chat events
      coachChatManager.removeEventListener('dataChannelOpen', handleStateChange)
      coachChatManager.removeEventListener('candidateTyping', handleStateChange)
      coachChatManager.removeEventListener(
        'availabilityChanged',
        handleStateChange,
      )
      coachChatManager.removeEventListener('chatDeclined', handleChatDeclined)

      // Remove listeners for list updates
      coachChatManager.removeEventListener('coachesUpdate', handleStateChange)
      coachChatManager.removeEventListener(
        'candidatesUpdate',
        handleStateChange,
      )
      coachChatManager.removeEventListener(
        'coachListUpdated',
        handleStateChange,
      )
      coachChatManager.removeEventListener(
        'candidateInfoUpdated',
        handleStateChange,
      )

      // Remove listener for candidate media state changes
      coachChatManager.removeEventListener(
        'candidateMediaStateChanged',
        handleCandidateMediaStateChange,
      )
    }
  }, [_sendWelcomeMessage, playRingtone, stopRingtone, playMessageNotification]) // Include dependencies

  // Initialize error logger
  useEffect(() => {
    if (props.log_chat_event_path) {
      errorLogger.initialize(
        coachChatManager.logEvent.bind(coachChatManager),
        props.conversationId,
      )
    }

    // Update conversation ID when it changes
    if (props.conversationId) {
      errorLogger.setConversationId(props.conversationId)
    }

    return () => {
      errorLogger.cleanup()
    }
  }, [props.log_chat_event_path, props.conversationId])

  // Initialize message notification audio element source on mount
  useEffect(() => {
    const settings = getMessageSoundSettings()
    if (messageNotificationRef.current && settings.url) {
      messageNotificationRef.current.src = settings.url
      messageNotificationRef.current.load()
    } else if (
      messageNotificationRef.current &&
      !settings.url &&
      props.messageSoundOptions &&
      props.messageSoundOptions.length > 0
    ) {
      // Fallback to first option if no saved preference
      messageNotificationRef.current.src = props.messageSoundOptions[0]
      messageNotificationRef.current.load()
    }
  }, [getMessageSoundSettings, props.messageSoundOptions])

  // Connect to chat system when component mounts
  useEffect(() => {
    if (props.socketUrl && props.username && props.chatRoom) {
      coachChatManager
        .connect(props.socketUrl, props.username, props.chatRoom, {
          socketUrl: props.socketUrl,
          username: props.username,
          chatRoom: props.chatRoom,
          inHours: props.inHours,
          logChatEventPath: props.log_chat_event_path,
          logChatMissedOpportunityPath: props.logChatMissedOpportunityPath,
          serverTime: props.serverTime,
          currentUserLocale: props.current_user?.locale,
          displayName: props.userDisplayName || props.username,
          firstName: props.userFirstName,
          abbreviatedName: props.userAbbreviatedName,
          isTechSupport: props.isTechSupport,
          coachLocales: props.coachLocales,
          videoEnabled: props.videoEnabled || false,
          browser: props.browserName || 'unknown',
          userLocale: props.userLocale || 'en',
          userLanguage: props.userLanguage || 'English',
          userCompany: props.userCompany,
          userCompanyId: props.userCompanyId,
          partnerCandidate: props.partnerCandidate || false,
          conversationStarter: props.conversationStarter,
          chatMode: props.chatMode || 'text',
          userId: props.userId,
          userEmail: props.userEmail,
          avatar: props.avatar,
          bigAvatar: props.bigAvatar,
          os: props.os,
          buildChatHistoryPath: props.buildChatHistoryPath,
          conversationId: props.conversationId,
          coachChatCandidateInfoPath: props.coach_chat_candidate_info_path,
          coachEndChatPath: props.coach_end_chat_path,
          isMobile: props.isMobile || false,
          clientTime: props.clientTime,
          timeOffset: props.timeOffset,
          deloitteUser: props.deloitteUser,
          userCareerDevelopment: props.userCareerDevelopment,
          opRedesign: props.opRedesign,
          enableAudio: false,
          enableVideo: false,
          enableDataChannels: false,
          autoInitUserMedia: false,
          debug: false,
          updateIntervalMs: 5000,
          appName: 'yns.coach_chat',
          presenceMessage: 'Coach Chat',
          isCoach: true, // Mark as coach
          availability: props.initialAvailability || 'away',
          coachBackgroundMode: 'none', // Initialize with default background mode // Initial availability state
          userMode: 'coach', // Set user mode for proper list handling
          chatHours: props.chatHours || '',
          hideChatHours: props.hideChatHours || false,
          modalCopy: props.modalCopy || {},
        })
        .then(() => {
          setIsOnline(true)
          return true
        })
        .catch((error) => {
          console.error('CoachChatProvider - Connection failed:', error)
          setIsOnline(false)
        })
    }

    return () => {
      // Cleanup connection when component unmounts
      if (coachChatManager.isConnected()) {
        coachChatManager.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only include props that should trigger a reconnection
    props.socketUrl,
    props.username,
    props.chatRoom,
    props.initialAvailability,
  ])

  useEffect(() => {
    const handleLogoutRequest = (event) => {
      const logoutUrl = event.detail?.logoutUrl || '/logout'

      try {
        // Force away even mid-chat so candidates cannot start new sessions (CTS-9831)
        coachChatManager.setAvailability('away', { force: true })
        window.dispatchEvent(
          new CustomEvent('coachAvailabilityChanged', {
            detail: { availability: 'away' },
          }),
        )
        // Tear down RTC in this tab so no orphaned live session remains
        if (coachChatManager.isConnected()) {
          coachChatManager.disconnect()
        }
      } catch (error) {
        console.error(
          'CoachChatContext - Error setting availability on logout:',
          error,
        )
      }

      window.dispatchEvent(
        new CustomEvent('coachLogoutReady', {
          detail: { logoutUrl },
        }),
      )
    }

    window.addEventListener('coachLogoutRequested', handleLogoutRequest)

    return () => {
      window.removeEventListener('coachLogoutRequested', handleLogoutRequest)
    }
  }, [])

  // Helper function to get human-readable chat status
  const getChatStatusMessage = useCallback(() => {
    const state = coachChatManager.getConnectionState()
    switch (state.chatStatus) {
      case 'disconnected':
        return 'Disconnected'
      case 'connecting':
        return 'Connecting to chat...'
      case 'waiting_for_candidate':
        return 'Waiting for candidate...'
      case 'chat_active':
        return 'Chat active with candidate'
      case 'chat_ended':
        return 'Chat ended'
      default:
        return 'Unknown status'
    }
  }, [])

  // Dark mode helper functions
  const getDarkModeClasses = useCallback(
    (lightClasses, darkClasses) => {
      return isDarkMode ? darkClasses : lightClasses
    },
    [isDarkMode],
  )

  const getBackgroundClass = useCallback(() => {
    return getDarkModeClasses('bg-white', 'bg-gray-800')
  }, [getDarkModeClasses])

  const getTextClass = useCallback(() => {
    return getDarkModeClasses('text-gray-900', 'text-white')
  }, [getDarkModeClasses])

  const getInputClass = useCallback(() => {
    return getDarkModeClasses(
      'border-neutral-300 bg-slate-50 text-gray-900 placeholder:text-gray-500 focus:border-green-800 focus:ring-green-800',
      'border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400',
    )
  }, [getDarkModeClasses])

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const newValue = !prev
      try {
        sessionStorage.setItem('coachChatDarkMode', JSON.stringify(newValue))
      } catch (error) {
        console.warn('Error saving coachChatDarkMode to sessionStorage:', error)
      }
      return newValue
    })
  }, [])

  // Memoize the bound methods to prevent recreation on every render
  const boundMethods = useMemo(
    () => ({
      acceptChat: coachChatManager.acceptChat.bind(coachChatManager),
      declineChat: coachChatManager.declineChat.bind(coachChatManager),
      endChat: coachChatManager.endChat.bind(coachChatManager),
      sendMessage: coachChatManager.sendMessage.bind(coachChatManager),
      sendTypingIndicator:
        coachChatManager.sendTypingIndicator.bind(coachChatManager),
      setAvailability: coachChatManager.setAvailability.bind(coachChatManager),
    }),
    [],
  )

  const contextValue = useMemo(
    () => ({
      // Provide direct access to coachChatManager
      chatManager: coachChatManager,

      // Current connection state (reactive)
      connectionState,

      // Coach-specific state
      isOnline,
      showCandidateList,
      setShowCandidateList,
      selectedCandidate,
      chatHistory,
      activeChats,
      pendingChats,

      // Helper functions
      getChatStatusMessage,
      stopRingtone,

      // Dark mode state and helpers
      isDarkMode,
      toggleDarkMode,
      getDarkModeClasses,
      getBackgroundClass,
      getTextClass,
      getInputClass,

      // Coach actions (memoized)
      ...boundMethods,

      // All the props for easy access
      ...props,
    }),
    [
      connectionState,
      isOnline,
      showCandidateList,
      selectedCandidate,
      chatHistory,
      activeChats,
      pendingChats,
      getChatStatusMessage,
      stopRingtone,
      isDarkMode,
      toggleDarkMode,
      getDarkModeClasses,
      getBackgroundClass,
      getTextClass,
      getInputClass,
      boundMethods,
      props,
    ],
  )

  return (
    <CoachChatContext.Provider value={contextValue}>
      {children}
      {/* Hidden audio elements for ringtone and message notifications */}
      <audio ref={ringtoneAudioRef} preload="auto" />
      <audio ref={messageNotificationRef} preload="auto" />
    </CoachChatContext.Provider>
  )
}
